import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/ui-extra";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { safeFormatDate } from "@/lib/dates";
import { toast } from "sonner";
import { CalendarRange, Plus, Trash2, Pencil, X, Loader2, TrendingUp } from "lucide-react";

/**
 * Seasons — its own home. Auto-detects season windows from the yard's
 * migration pattern (tap a suggestion to create instantly), lets you add /
 * remove seasons manually, compare any two, and see per-season workload +
 * rider dedication. Backed by /api/seasons/* (gated by `trends:read`).
 */
export default function SeasonsPage() {
  const [seasons, setSeasons] = useState([]);
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [managing, setManaging] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [cmp, setCmp] = useState({ a: null, b: null });
  const [form, setForm] = useState({ name: "", start_date: "", end_date: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, p] = await Promise.all([api.get("/seasons/analysis"), api.get("/seasons/presets")]);
      setSeasons(a.data?.seasons || []);
      setPresets(p.data?.presets || []);
    } catch (_e) { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const applyPreset = async (p) => {
    const sMon = safeFormatDate(p.start_date, "MMM");
    const eMon = safeFormatDate(p.end_date, "MMM");
    const sYr = safeFormatDate(p.start_date, "yyyy");
    const eYr = safeFormatDate(p.end_date, "yyyy");
    const name = sYr === eYr ? `Season ${sMon}\u2013${eMon} ${sYr}` : `Season ${sMon} ${sYr}\u2013${eMon} ${eYr}`;
    try {
      await api.post("/seasons", { name, start_date: p.start_date, end_date: p.end_date });
      toast.success(`Created \u201c${name}\u201d`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create season");
    }
  };

  const add = async () => {
    if (!form.name.trim() || !form.start_date || !form.end_date) {
      toast.error("Name, start and end dates are required"); return;
    }
    setSaving(true);
    try {
      await api.post("/seasons", form);
      setForm({ name: "", start_date: "", end_date: "" });
      toast.success("Season added");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add season");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/seasons/${id}`); toast.success("Season removed"); load(); }
    catch (_e) { toast.error("Could not remove"); }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="seasons-page">
      <PageHeader
        title="Seasons"
        subtitle="Auto-detected from your migration pattern — create, compare and track each season"
        actions={
          <div className="flex items-center gap-1">
            {seasons.length >= 2 && (
              <Button variant="ghost" size="sm" onClick={() => setComparing((c) => !c)} data-testid="seasons-compare-toggle">
                {comparing ? "Hide compare" : "Compare"}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setManaging((m) => !m)} data-testid="seasons-manage-toggle">
              {managing ? <X className="h-4 w-4 mr-1" /> : <Pencil className="h-4 w-4 mr-1" />}
              {managing ? "Close" : "Manage"}
            </Button>
          </div>
        }
      />

      <Card className="rounded-xl" data-testid="seasons-card">
        <CardContent className="pt-6">
          <p className="text-xs text-muted-foreground mb-4">
            We auto-detect seasons from the yard's migration pattern (big moves in and out of work) &mdash; tap a suggestion under <strong>Manage</strong> to create one instantly, or add your own. Each season shows rides logged, days in work vs resting, how many horses moved in and out of work, and rider dedication.
          </p>

          {managing && (
            <div className="mb-5 rounded-lg border p-3 space-y-3" data-testid="seasons-manager">
              {presets.length > 0 && (
                <div className="space-y-1.5" data-testid="season-presets">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    Auto-detected from migrations &mdash; tap to create
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {presets.map((p) => (
                      <button key={`${p.start_date}_${p.end_date}`} onClick={() => applyPreset(p)}
                        className="rounded-full border border-[hsl(var(--ap-brass)/0.4)] bg-[hsl(var(--ap-brass)/0.08)] px-2.5 py-1 text-[11px] hover:bg-[hsl(var(--ap-brass)/0.16)] transition-colors"
                        data-testid={`season-preset-${p.start_date}`}>
                        <span className="font-medium">{safeFormatDate(p.start_date, "d MMM")} &rarr; {safeFormatDate(p.end_date, "d MMM yyyy")}</span>
                        {p.into_work ? <span className="ml-1 opacity-70">&middot; {p.into_work} into work</span> : null}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid gap-2 sm:grid-cols-4 items-end">
                <div className="sm:col-span-2">
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Name</label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="December Season" data-testid="season-name-input" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Start</label>
                  <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="season-start-input" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground">End</label>
                  <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="season-end-input" />
                </div>
              </div>
              <Button size="sm" onClick={add} disabled={saving} data-testid="season-add-btn"
                className="bg-[hsl(var(--ap-forest))] hover:bg-[hsl(var(--ap-forest))]/90">
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                Add season
              </Button>
            </div>
          )}

          {comparing && seasons.length >= 2 && (
            <SeasonCompare seasons={seasons} cmp={cmp} setCmp={setCmp} />
          )}

          {loading ? (
            <div className="space-y-3">{[0, 1].map((i) => <Skeleton key={i} className="h-24 w-full" />)}</div>
          ) : seasons.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2" data-testid="seasons-empty">
              No seasons yet. Tap <strong>Manage</strong> to add your first one — or apply an auto-detected suggestion.
            </p>
          ) : (
            <div className="space-y-3" data-testid="seasons-list">
              {seasons.map((s) => (
                <div key={s.id} className="rounded-lg border p-4" data-testid={`season-${s.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-medium">{s.name}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {safeFormatDate(s.start_date, "d MMM yyyy")} &ndash; {safeFormatDate(s.end_date, "d MMM yyyy")}
                      </div>
                    </div>
                    {managing && (
                      <button onClick={() => remove(s.id)} className="text-muted-foreground hover:text-destructive p-1" data-testid={`season-delete-${s.id}`} aria-label="Delete season">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <div className="mt-3 grid grid-cols-3 sm:grid-cols-6 gap-3 text-center">
                    <Stat label="Rides" value={s.total_rides} />
                    <Stat label="In work" value={s.horses_worked} sub="horses" />
                    <Stat label="Rested" value={s.horses_rested} sub="horses" />
                    <Stat label="Work days" value={s.work_days} />
                    <Stat label="Rest days" value={s.rest_days} />
                    <Stat label="Avg rides" value={s.avg_rides_per_worked_horse} />
                  </div>
                  {(s.moved_to_work > 0 || s.moved_to_rest > 0) && (
                    <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                      {s.moved_to_work > 0 && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--ap-forest)/0.1)] text-[hsl(var(--ap-forest))] px-2 py-0.5">
                          <TrendingUp className="h-3 w-3" /> {s.moved_to_work} moved into work
                        </span>
                      )}
                      {s.moved_to_rest > 0 && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--ap-saddle)/0.12)] text-[hsl(var(--ap-saddle))] px-2 py-0.5">
                          {s.moved_to_rest} moved to rest
                        </span>
                      )}
                    </div>
                  )}
                  {s.riders?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-border/40" data-testid={`season-riders-${s.id}`}>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Riders</div>
                      <div className="space-y-1">
                        {s.riders.slice(0, 5).map((r) => (
                          <div key={r.rider} className="flex items-center justify-between text-xs">
                            <span className="font-medium">{r.rider}</span>
                            <span className="text-muted-foreground tabular-nums">
                              {r.sessions} rides{r.avg_score != null ? ` · ${r.avg_score} avg` : ""}
                              {r.dedication != null ? ` · ${r.dedication_label} ${r.dedication}` : ""}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div>
      <div className="font-serif text-2xl tabular-nums leading-none">{value ?? 0}</div>
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground mt-1">{label}{sub ? ` ${sub}` : ""}</div>
    </div>
  );
}

function SeasonCompare({ seasons, cmp, setCmp }) {
  const a = seasons.find((s) => s.id === cmp.a) || seasons[0];
  const b = seasons.find((s) => s.id === cmp.b) || seasons[1];
  const METRICS = [
    ["Total rides", "total_rides"],
    ["Horses in work", "horses_worked"],
    ["Horses rested", "horses_rested"],
    ["Work days", "work_days"],
    ["Rest days", "rest_days"],
    ["Avg rides / horse", "avg_rides_per_worked_horse"],
    ["Moved into work", "moved_to_work"],
    ["Moved to rest", "moved_to_rest"],
  ];
  const Picker = ({ side }) => (
    <div className="flex flex-wrap gap-1">
      {seasons.map((s) => {
        const active = cmp[side] ? cmp[side] === s.id : (side === "a" ? a?.id === s.id : b?.id === s.id);
        return (
          <button key={s.id} onClick={() => setCmp((c) => ({ ...c, [side]: s.id }))}
            className={`rounded-full px-2 py-0.5 text-[11px] border ${active ? "bg-[hsl(var(--ap-forest))] text-white border-transparent" : "border-border text-muted-foreground"}`}
            data-testid={`compare-${side}-${s.id}`}>
            {s.name}
          </button>
        );
      })}
    </div>
  );
  return (
    <div className="mb-5 rounded-lg border p-4" data-testid="seasons-compare">
      <div className="grid grid-cols-2 gap-4 mb-3">
        <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Season A</div><Picker side="a" /></div>
        <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Season B</div><Picker side="b" /></div>
      </div>
      <div className="grid grid-cols-[1fr_auto_auto] gap-x-4 gap-y-1 text-sm">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground" />
        <div className="text-right font-medium truncate max-w-[120px]">{a?.name}</div>
        <div className="text-right font-medium truncate max-w-[120px]">{b?.name}</div>
        {METRICS.map(([label, key]) => {
          const av = a?.[key] ?? 0, bv = b?.[key] ?? 0;
          return (
            <div key={key} className="contents">
              <div className="text-muted-foreground border-t border-border/40 py-1">{label}</div>
              <div className={`text-right tabular-nums border-t border-border/40 py-1 ${av >= bv ? "font-semibold text-[hsl(var(--ap-forest))]" : ""}`}>{av}</div>
              <div className={`text-right tabular-nums border-t border-border/40 py-1 ${bv > av ? "font-semibold text-[hsl(var(--ap-forest))]" : ""}`}>{bv}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
